import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { Loader2 } from 'lucide-react';

const AnnouncementsList = ({ workshop, onBack, onAddNew }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnnouncements();
  }, [workshop.id]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('workshop_announcements')
        .select('*')
        .eq('workshop_id', workshop.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAnnouncements(data || []);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (announcementId) => {
    if (!confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('workshop_announcements')
        .delete()
        .eq('id', announcementId);

      if (deleteError) throw deleteError;

      // Refresh the list
      fetchAnnouncements();
      alert('Announcement deleted successfully!');
    } catch (err) {
      console.error('Error deleting announcement:', err);
      alert('Failed to delete announcement: ' + err.message);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-4"
        >
          <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
          Back to Workshops
        </Button>
        
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Announcements</h2>
            <p className="text-muted-foreground mt-1">
              {workshop.title}
            </p>
          </div>
          <Button onClick={onAddNew}>
            <Icon name="Plus" className="w-4 h-4 mr-2" />
            Add Announcement
          </Button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
          <p className="text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}

      {/* Empty State */}
      {!loading && announcements.length === 0 && (
        <div className="text-center py-12 bg-card rounded-lg border border-border">
          <Icon name="MessageSquare" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No announcements yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first announcement for this workshop
          </p>
          <Button onClick={onAddNew}>
            <Icon name="Plus" className="w-4 h-4 mr-2" />
            Add Announcement
          </Button>
        </div>
      )}

      {/* Announcements List */}
      {!loading && announcements.length > 0 && (
        <div className="space-y-4">
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              className="bg-card rounded-lg border border-border p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-1">
                    {announcement.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(announcement.created_at)}
                  </p>
                </div>
                <button
                  onClick={() => handleDelete(announcement.id)}
                  className="p-2 hover:bg-destructive/10 text-destructive rounded-lg transition-colors"
                  title="Delete announcement"
                >
                  <Icon name="Trash2" className="w-5 h-5" />
                </button>
              </div>
              
              <div className="text-foreground whitespace-pre-wrap leading-relaxed">
                {announcement.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AnnouncementsList;
